import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from '../../contexts/ThemeContext';
import "./Styling.css";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MIN_DATE = new Date(2025, 2, 21);
const DateRangePicker = ({
  startDate: propStartDate,
  endDate: propEndDate,
  onChange,
}) => {
  const { theme } = useTheme();
  const today = new Date();
  const initialMonth = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [nextMonth, setNextMonth] = useState(
    new Date(initialMonth.getFullYear(), initialMonth.getMonth() + 1, 1)
  );

  const calendarRef = useRef(null);

  const [tempStartDate, setTempStartDate] = useState(propStartDate || null);
  const [tempEndDate, setTempEndDate] = useState(propEndDate || null);

  useEffect(() => {
    if (propStartDate) setTempStartDate(propStartDate);
  }, [propStartDate]);

  useEffect(() => {
    if (propEndDate) setTempEndDate(propEndDate);
  }, [propEndDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const applySelection = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Please select both start and end dates.");
      return;
    }
    if (tempEndDate < tempStartDate) {
      alert("End date cannot be before start date.");
      return;
    }
    onChange?.({ startDate: tempStartDate, endDate: tempEndDate });
    setIsOpen(false);
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.setMonth(currentMonth.getMonth() + 1))
    );
    setNextMonth(new Date(nextMonth.setMonth(nextMonth.getMonth() + 1)));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.setMonth(currentMonth.getMonth() - 1))
    );
    setNextMonth(new Date(nextMonth.setMonth(nextMonth.getMonth() - 1)));
  };

  const formatDate = (date) => {
    if (!date) return "";
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${MONTHS[month - 1].substring(0, 3)} ${day}, ${year}`;
  };

  const getDisplayValue = () => {
    if (tempStartDate && tempEndDate) {
      return `${formatDate(tempStartDate)} - ${formatDate(tempEndDate)}`;
    } else if (tempStartDate) {
      return `${formatDate(tempStartDate)} -`;
    }
    return "Select date range";
  };
  const isBeforeMinDate = (day, month, year) => {
    const date = new Date(year, month, day);
    return date < MIN_DATE;
  };
  const generateMonthDays = (monthDate) => {
    const month = monthDate.getMonth();
    const year = monthDate.getFullYear();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const previousMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: previousMonthLastDay - i,
        month: month - 1,
        year,
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handleDateClick = (day, month, year) => {
    if (isBeforeMinDate(day, month, year)) return;

    const clickedDate = new Date(year, month, day);

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(clickedDate);
      setTempEndDate(null);
    } else if (clickedDate < tempStartDate) {
      setTempStartDate(clickedDate);
      setTempEndDate(null);
    } else {
      setTempEndDate(clickedDate);
    }
  };
  const resetSelection = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    const resetTo = new Date();
    setCurrentMonth(resetTo);
    setNextMonth(new Date(resetTo.getFullYear(), resetTo.getMonth() + 1, 1));
    onChange?.({ startDate: null, endDate: null });
  };

  const isDateInRange = (day, month, year) => {
    if (!tempStartDate || !tempEndDate) return false;

    const date = new Date(year, month, day);
    return date >= tempStartDate && date <= tempEndDate;
  };

  const isStartDate = (day, month, year) => {
    if (!tempStartDate) return false;
    const date = new Date(year, month, day);
    return date.getTime() === tempStartDate.getTime();
  };

  const isEndDate = (day, month, year) => {
    if (!tempEndDate) return false;
    const date = new Date(year, month, day);
    return date.getTime() === tempEndDate.getTime();
  };

  const renderCalendar = (monthDate) => {
    const days = generateMonthDays(monthDate);
    const month = monthDate.getMonth();
    const year = monthDate.getFullYear();

    return (
      <div className="date-picker-calendar">
        <div className="date-picker-month-header">
          {MONTHS[month]} {year}
        </div>
        <div className="date-picker-weekdays">
          {DAYS.map((day) => (
            <div key={day} className="date-picker-weekday">
              {day}
            </div>
          ))}
        </div>
        <div className="date-picker-days">
          {days.map((day, index) => {
            const isDisabled = isBeforeMinDate(day.day, day.month, day.year);
            return (
              <div
                key={`${day.month}-${day.day}-${index}`}
                className={`
                  date-picker-day 
                  ${!day.isCurrentMonth ? "date-picker-day-faded" : ""}
                  ${
                    isStartDate(day.day, day.month, day.year)
                      ? "date-picker-day-start"
                      : ""
                  }
                  ${
                    isEndDate(day.day, day.month, day.year)
                      ? "date-picker-day-end"
                      : ""
                  }
                  ${
                    isDateInRange(day.day, day.month, day.year)
                      ? "date-picker-day-in-range"
                      : ""
                  }
                  ${isDisabled ? "date-picker-day-disabled" : ""}
                `}
                onClick={() =>
                  !isDisabled && handleDateClick(day.day, day.month, day.year)
                }
              >
                {day.day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`date-range-picker-container ${theme?.isDarkMode ? 'dark-theme' : ''}`}>
      <div className="date-range-picker">
        <strong>Filter by date range</strong>
        <button
          className="date-range-picker-input"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="date-picker-icon">📅</span>
          <span className="date-picker-value">{getDisplayValue()}</span>
        </button>

        {isOpen && (
          <div className={`date-picker-dropdown ${theme?.isDarkMode ? 'dark-theme-dropdown' : ''}`} ref={calendarRef}>
            <div className="date-picker-navigation">
              <button
                className="date-picker-nav-button"
                onClick={handlePrevMonth}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                className="date-picker-nav-button"
                onClick={handleNextMonth}
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="date-picker-calendars">
              {renderCalendar(currentMonth)}
              {renderCalendar(nextMonth)}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 12px",
              }}
            >
              <button
                onClick={resetSelection}
                className="date-range-picker-reset-button"
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Reset
              </button>
              <button
                onClick={applySelection}
                className="date-range-picker-apply-button"
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
                disabled={!tempStartDate || !tempEndDate}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
